import { ActionTree } from "vuex";
import { OpenAccountInvoiceType, OpenAccountState } from "@/feature/OpenAccount/store/interface";
import { IStoreRootState } from "@/store/interface";
import { OpenAccountAction, OpenAccountGetter, OpenAccountMutation } from "@/feature/OpenAccount/store/constants";
import {
	$createOpenAccountInvoice,
	$getLegalEntityInfo,
	$getOpenAccountInvoice,
	$getOpenAccountInvoiceList,
	$sendOpenAccountConfirmationCode,
	$verifyOpenAccountConfirmationCode
} from "@/feature/OpenAccount/api/open-account";
import {
	getDataToGetClientInfo,
	getDataToGetInvoice,
	getDataToGetInvoiceList,
	getDataToSendCode,
	getDataToVerifyCode
} from "@/feature/OpenAccount/store/utils";

const LOADING_HINT_CHANGE_INTERVAL_IN_SECONDS = 5;

export default <ActionTree<OpenAccountState, IStoreRootState>> {

	[OpenAccountAction.loadInvoiceList]: async (
		{ state: { requestData }, commit, dispatch }
	) => {
		if (!requestData) return;

		const invoiceList = await $getOpenAccountInvoiceList(
			getDataToGetInvoiceList(requestData)
		);

		for (let i = 0; i < invoiceList.length; i++) {
			if (invoiceList[i].applicationStatus === OpenAccountInvoiceType.OPEN) {
				commit(OpenAccountMutation.setInvoice, invoiceList[i]);
				await dispatch(OpenAccountAction.loadInvoice);

				break;
			}
		}

		commit(OpenAccountMutation.setInvoiceList, invoiceList);
	},

	[OpenAccountAction.loadInvoice]: async (
		{ commit, state: { invoice } }
	) => {
		if (!invoice) return;

		const response = await $getOpenAccountInvoice(
			getDataToGetInvoice(invoice)
		);

		commit(OpenAccountMutation.setInvoice, response);
	},

	[OpenAccountAction.sendConfirmationCode]: async (
		{ state: { requestData }, commit }
	): Promise<boolean | undefined> => {
		if (!requestData) return;

		const response = await $sendOpenAccountConfirmationCode(
			getDataToSendCode(requestData)
		);

		commit(OpenAccountMutation.setPhoneNumber, response);

		return !!response;
	},

	[OpenAccountAction.verifyConfirmationCode]: async (
		{ state: { requestData } }
	): Promise<boolean | undefined> => {
		if (requestData)
			return $verifyOpenAccountConfirmationCode(getDataToVerifyCode(requestData));
	},

	[OpenAccountAction.loadLegalEntity]: async (
		{ commit, getters, state: { requestData } }
	): Promise<boolean | undefined> => {
		if (!requestData) return;

		commit(
			OpenAccountMutation.setLegalEntityLoadingHint,
			getters[OpenAccountGetter.legalEntityLoadingHintList][0]
		);

		commit(OpenAccountMutation.setLegalEntityLoading, true);

		let hintIndex = 0;

		const loadingHintChangeIntervalId = setInterval(() => {
			const hintsCount = getters[OpenAccountGetter.legalEntityLoadingHintList].length;

			if ((hintIndex + 1) < hintsCount) hintIndex++;

			commit(
				OpenAccountMutation.setLegalEntityLoadingHint,
				getters[OpenAccountGetter.legalEntityLoadingHintList][hintIndex]
			);
		}, LOADING_HINT_CHANGE_INTERVAL_IN_SECONDS * 1000);

		const response = await $getLegalEntityInfo(getDataToGetClientInfo(requestData));

		commit(OpenAccountMutation.setLegalEntity, response);
		commit(OpenAccountMutation.setLegalEntityLoading, false);

		clearInterval(loadingHintChangeIntervalId);

		return !!response;
	},

	[OpenAccountAction.createApplication]: async (
		{ state: { requestData }, commit }
	): Promise<boolean | undefined> => {
		if (!requestData) return;

		const response = await $createOpenAccountInvoice(
			getDataToGetInvoiceList(requestData)
		);

		commit(OpenAccountMutation.setInvoice, response);

		return !!response;
	},

}